import React, { useEffect, useState } from 'react';
import { ClipboardList, Layers, PackageSearch, Ruler, Workflow } from 'lucide-react';
import * as api from '../../api';
import { RNLLoader } from '../UI';
import { Panel, StatTile } from '../materialTakeoff/MTOShared';
import { NAVY, fmtQty } from './mmUtils';

const SECTIONS = [
  { tab: 'register', Icon: ClipboardList, title: 'Mapping Register', text: 'Every BOQ line of the project with its materials and approval status.' },
  { tab: 'unmapped', Icon: PackageSearch, title: 'Unmapped Materials', text: 'Materials requested, ordered or used on site that no approved mapping covers.' },
  { tab: 'mapped', Icon: Layers, title: 'Mapped Materials', text: 'Review, trace and export every BOQ line ↔ material link.' },
  { tab: 'site-measurement', Icon: Ruler, title: 'Site Measurement', text: 'BOQ progress read from QS installation records — no manual entry.' },
];

const FLOW = ['BOQ', 'Work Package', 'Materials', 'Mapping Approval', 'Material Request', 'PO', 'Use on Site (DMR)', 'QS Installation', 'BOQ Progress'];

export default function MMDashboard({ projectId, goTo }) {
  const [s, setS] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    setS(null);
    api.getMappingSummary(projectId).then(setS).catch(e => { setError(e.message || 'Could not load the dashboard.'); setS(false); });
  }, [projectId]);

  if (s === null) return <RNLLoader title="Loading dashboard..." subtitle="Reading BOQ lines and mappings" />;
  if (!s) return <div style={{ padding: 14, borderRadius: 10, background: '#fee2e2', color: '#991b1b', fontSize: 13 }}>{error}</div>;

  const by = s.by_status || {};
  const tiles = [
    { label: 'BOQ Lines', value: s.boq_items, accent: NAVY, go: ['register'] },
    { label: 'Work Packages', value: s.work_packages, accent: '#334155' },
    { label: 'Mapped (Approved)', value: s.mapped_lines, accent: '#16a34a', go: ['register', { status: 'approved' }] },
    { label: 'Not Mapped', value: s.unmapped_lines, accent: '#dc2626', go: ['register', { status: 'unmapped' }] },
    { label: 'Pending Approval', value: by.pending_approval || 0, accent: '#ca8a04', go: ['register', { status: 'pending_approval' }] },
    { label: 'Draft / Returned', value: (by.draft || 0) + (by.returned || 0) + (by.rejected || 0), accent: '#ea580c', go: ['register', { status: 'in_progress' }] },
    { label: 'Mapped Materials', value: s.mapped_materials, accent: '#7c3aed', go: ['mapped'] },
    { label: 'Unmapped Materials', value: s.unmapped_materials, accent: '#b91c1c', go: ['unmapped'] },
    { label: 'Non-BOQ', value: s.non_boq, accent: '#6d28d9', go: ['unmapped'] },
    { label: 'Variation', value: s.variation, accent: '#0369a1', go: ['unmapped'] },
    { label: 'Used / Not Accounted', value: s.used_not_accounted, accent: '#be123c', go: ['unmapped'] },
    { label: 'BOQ Progress', value: `${s.boq_progress_pct}%`, accent: '#0f766e', go: ['site-measurement'] },
  ];
  const coverage = s.boq_items ? Math.round((s.mapped_lines / s.boq_items) * 1000) / 10 : 0;

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 20 }}>
        {tiles.map(t => <StatTile key={t.label} {...t} onClick={t.go ? () => goTo(...t.go) : undefined} />)}
      </div>

      <Panel title={`Mapping coverage — ${coverage}% of BOQ lines approved`}>
        <div style={{ height: 12, borderRadius: 999, background: 'var(--bg-base)', border: '1px solid var(--border-light)', overflow: 'hidden', display: 'flex' }}>
          {[['approved', '#16a34a'], ['pending_approval', '#eab308'], ['draft', '#94a3b8'], ['returned', '#f97316'], ['rejected', '#ef4444']].map(([k, c]) => (
            (by[k] || 0) > 0 && <div key={k} title={`${k.replace('_', ' ')}: ${by[k]}`} style={{ width: `${(by[k] / s.boq_items) * 100}%`, background: c }} />
          ))}
        </div>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 8, fontSize: 11.5, color: 'var(--text-secondary)' }}>
          <span>🟩 Approved {by.approved || 0}</span><span>🟨 Pending {by.pending_approval || 0}</span>
          <span>⬜ Draft {by.draft || 0}</span><span>🟧 Returned {by.returned || 0}</span><span>🟥 Rejected {by.rejected || 0}</span>
          <span>Not mapped {s.unmapped_lines}</span><span>{s.mapping_relationships} BOQ line ↔ material links</span>
        </div>
      </Panel>

      <Panel title="Work package progress (from QS installation records)">
        {s.work_packages_progress.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>This project has no current BOQ lines yet.</div>
        ) : (
          <div style={{ display: 'grid', gap: 6, maxHeight: 420, overflowY: 'auto' }}>
            {s.work_packages_progress.map(w => (
              <div key={w.dmr_subsection_id || w.work_package}
                   style={{ display: 'grid', gridTemplateColumns: 'minmax(140px, 1.3fr) 2fr 60px minmax(150px, 1fr)', alignItems: 'center', gap: 12, fontSize: 12.5 }}>
                <span style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={w.work_package}>{w.work_package}</span>
                <div style={{ height: 10, borderRadius: 999, background: 'var(--bg-base)', border: '1px solid var(--border-light)', overflow: 'hidden' }}>
                  <div style={{ width: `${w.progress_pct}%`, height: '100%', background: NAVY }} />
                </div>
                <span style={{ fontWeight: 800, textAlign: 'right' }}>{w.progress_pct}%</span>
                <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                  {fmtQty(w.installed)} / {fmtQty(w.quantity)} · {w.lines} BOQ · {w.mapped_lines} mapped
                </span>
              </div>
            ))}
          </div>
        )}
      </Panel>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 12, marginBottom: 16 }}>
        {SECTIONS.map(({ tab, Icon, title, text }) => (
          <button key={tab} onClick={() => goTo(tab)} style={{
            textAlign: 'left', background: 'var(--bg-card)', border: '1px solid black', borderRadius: 12, padding: 16, cursor: 'pointer',
            display: 'flex', flexDirection: 'column', gap: 8, color: 'var(--text-primary)', fontFamily: 'var(--font)',
          }}>
            <span style={{ width: 36, height: 36, borderRadius: 9, background: 'rgba(11,49,139,0.1)', color: NAVY, display: 'grid', placeItems: 'center' }}><Icon size={19} /></span>
            <span style={{ fontWeight: 800, fontSize: 14 }}>{title}</span>
            <span style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.45 }}>{text}</span>
          </button>
        ))}
      </div>

      <div style={{ border: '1px solid black', borderRadius: 12, padding: '14px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 800, fontSize: 13, marginBottom: 10 }}><Workflow size={16} /> How it flows</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
          {FLOW.map((step, i) => (
            <React.Fragment key={step}>
              <span style={{ fontSize: 11.5, fontWeight: 700, padding: '5px 11px', borderRadius: 8, background: i < 4 ? 'rgba(11,49,139,0.1)' : 'var(--bg-base)',
                             color: i < 4 ? NAVY : 'var(--text-secondary)', border: '1px solid var(--border-light)' }}>{step}</span>
              {i < FLOW.length - 1 && <span style={{ color: '#94a3b8' }}>→</span>}
            </React.Fragment>
          ))}
        </div>
        <p style={{ margin: '10px 0 0', fontSize: 12, color: 'var(--text-secondary)' }}>
          Mappings are approved in the approval engine (Workflow Config → <b>BOQ_MATERIAL_MAPPING</b>). Requests, purchase orders and DMR
          transactions are read as they are — nothing is entered twice.
        </p>
      </div>
    </div>
  );
}
